import { useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/scss';
import 'swiper/scss/navigation';
import 'swiper/scss/pagination';

import { EventType } from '../../../../types/events/common.types';
import { ReferralAnalyticsEvent } from '../../types/';

import ArrowLeft from 'assets/icons/arrow-left.svg';
import ArrowRight from 'assets/icons/arrow-right.svg';

import { AnalyticsEventItem } from './AnalyticsEventItem';
import styles from './AnalyticsEventsSection.module.scss';

type AnalyticsEventsSectionProps = {
  analyticsEvents: ReferralAnalyticsEvent[];
};

const AnalyticsEventsSection = ({ analyticsEvents }: AnalyticsEventsSectionProps) => {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const itemCount = analyticsEvents.length;
  const shouldShowArrows = itemCount >= 5;
  const shouldCenterSlides = itemCount <= 3;
  const isSingleEvent = itemCount === 1;

  const handleSlideChange = (swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  return (
    <div className="tw-relative tw-mx-auto tw-w-full tw-max-w-[1280px] tw-font-jost lg:tw-px-0">
      {shouldShowArrows && (
        <>
          <button
            ref={prevRef}
            disabled={isBeginning}
            className={styles['arrow-button-left']}
            aria-label="Previous slide"
          >
            <img src={ArrowLeft} alt="Previous" />
          </button>

          <button ref={nextRef} disabled={isEnd} className={styles['arrow-button-right']} aria-label="Next slide">
            <img src={ArrowRight} alt="Next" />
          </button>
        </>
      )}

      <Swiper
        // @ts-ignore
        modules={[Navigation, Pagination]}
        spaceBetween={16}
        slidesPerView={1.3}
        centeredSlides={true}
        centeredSlidesBounds={true}
        navigation={
          shouldShowArrows
            ? {
                prevEl: prevRef.current,
                nextEl: nextRef.current,
              }
            : false
        }
        onBeforeInit={(swiper) => {
          if (shouldShowArrows && typeof swiper.params.navigation !== 'boolean') {
            const navigation = swiper.params.navigation;
            if (navigation) {
              navigation.prevEl = prevRef.current;
              navigation.nextEl = nextRef.current;
            }
          }
        }}
        onSwiper={(swiper) => {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        }}
        onSlideChange={handleSlideChange}
        pagination={{
          clickable: true,
        }}
        breakpoints={{
          768: {
            slidesPerView: 2.4,
            spaceBetween: 24,
            centeredSlides: false,
          },
          1024: {
            slidesPerView: 4,
            spaceBetween: 44,
            centeredSlides: false,
          },
        }}
        className={`${styles['swiper']} ${shouldCenterSlides ? styles['centered'] : ''} ${
          isSingleEvent ? styles['single-event'] : ''
        }`}
      >
        {analyticsEvents.map((event) => (
          <SwiperSlide key={event.id}>
            <AnalyticsEventItem
              id={event.id}
              name={event.name}
              imageUrl={event.imageUrl}
              isVip={event.isVip}
              isUpcoming={event.isUpcoming}
              eventType={event.eventType}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default AnalyticsEventsSection;
